"use client";

import {toolRequestBody} from "./toolRequestBody";

import clsx from "clsx";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {ApprovalStatusBanner, approvalVisualState, approvalBannerLabel} from "@repo/ui/gailgpt/approval-status-banner";
import type {ToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import type {ApprovalPart} from "@/types/chatMessages";
import Icon from "@/components/library/Icon";
import {approvalStatusKey} from "./approvalPresentation";
import {changedFields} from "./ScriptSnapshotDiff";
import ProposalFieldLabel from "./ProposalFieldLabel";
import {actionFieldLabel} from "./useCaseSnapshot";
import {proposalTitle, proposalTypeKey} from "./proposalIdentity";
import {selectionClass} from "./ApprovalDecisionStatus";

export function reviewSummary(parts: ApprovalPart[], title: string, status: ToolApprovalStatus) {
   const summaryProposal = parts.length === 1 ? parts[0].data.approval.proposal : undefined;
   const parameters = parts[0].data.parameters;
   const body = toolRequestBody(parameters);
   const subject = parts.length === 1 ? [body.title, body.name].find((value): value is string => typeof value === "string" && !!value.trim()) : undefined;
   const objectTitle = summaryProposal ? proposalTitle(summaryProposal) : subject ?? title;
   const statusKey = parts.length === 1 ? approvalStatusKey(parts[0].data, status) : `toolApproval.${status}`;
   const fields = summaryProposal ? changedFields(summaryProposal) : [];

   return {summaryProposal, objectTitle, statusKey, fields};
}

export default function ApprovalReviewSummary({summary, queued, status, current, isOpen, showFields, onOpen, children}: {
   summary: ReturnType<typeof reviewSummary>;
   queued: boolean | null;
   status: ToolApprovalStatus;
   current: boolean;
   isOpen: boolean;
   showFields: boolean;
   onOpen: () => Promise<void>;
   children: ReactNode;
}) {
   const {t} = useTranslation("ask");
   const {summaryProposal, objectTitle, statusKey, fields} = summary;
   return <section aria-label={t("toolApproval.title")} data-approval-state={approvalVisualState(status, queued)} className={clsx("approval-card my-3 overflow-clip rounded-xl border", selectionClass(queued), (current || isOpen) && "ring-1 ring-primary/50")}>
      <ApprovalStatusBanner state={approvalVisualState(status, queued)}>
         {t(approvalBannerLabel(status, queued, statusKey))}
      </ApprovalStatusBanner>
      <button type="button" aria-expanded={current || isOpen}
         aria-current={current || isOpen ? "true" : undefined}
         onClick={onOpen}
         className="group block w-full rounded-xl text-left transition-colors hover:bg-100/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
         <span className="flex items-start gap-3 px-4 py-3.5">
            <Icon icon={summaryProposal?.capability === "use_cases_update" ? "list-check" : "file-lines"} className="mt-1 h-4 w-4 shrink-0 text-500" />
            <span className="min-w-0 flex-1">
               <span className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-500">
                  {summaryProposal && <span>{t(proposalTypeKey(summaryProposal))}</span>}
                  {current && <span className="font-semibold text-primary">{t("toolApproval.currentReview")}</span>}
               </span>
               <span className="approval-title block break-words">{objectTitle}</span>
               {showFields && fields.length > 0 && <span className="mt-1 block text-xs leading-5 text-500">
                  {fields.slice(0, 3).map((field, index) => <span key={field}>
                     {index > 0 && <span aria-hidden> · </span>}
                     <ProposalFieldLabel field={summaryProposal?.capability === "use_cases_update" ? actionFieldLabel(field) : field} />
                  </span>)}
                  {fields.length > 3 && <span> +{fields.length - 3}</span>}
               </span>}
               <span className="approval-open mt-2 inline-flex items-center gap-2 text-approval-body font-medium">
                  {t(status === "completed" ? "toolApproval.viewChanges" : "toolApproval.review")}
                  <Icon icon="arrow-right" className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
               </span>
            </span>
         </span>
      </button>
      {children}
   </section>;
}
